/*
 * @format
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import config from '../env';

// These are currently duplicated in the mock
export const States = {
  Initial: 'Initial',
  Open: 'Open',
  Reconnecting: 'Waiting',
  AttemptingReconnect: 'Reconnecting',
  Closed: 'Closed',
  Disabled: 'Disabled',
};

const initialDelay = 100;
const maxRetries = 7;
const connectTimeoutMs = 10000;

export default class WebSocketClient {
  constructor(url) {
    this.url = url;
    this.onStateChangeListener = null;
    this.onMessageListener = null;
    this.retryCounter = 0;
    this.errorCounter = 0;

    this.open();
  }

  async open() {
    this.reconnectEnabled = true;
    this.retryTimeout = clearTimeout(this.retryTimeout);
    this.connectTimeout = clearTimeout(this.connectTimeout);

    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No idToken in auth session');
      }

      const server = `${config.halo_websocket_api_endpoint}?token=${token}`;

      this.updateStateListeners(States.AttemptingReconnect);
      this.ws = new WebSocket(server);
      this.ws.onclose = this.onCloseCallback.bind(this);
      this.ws.onerror = this.onErrorCallback.bind(this);
      this.ws.onmessage = this.onMessageCallback.bind(this);
      this.ws.onopen = this.onOpenCallback.bind(this);

      this.connectTimeout = setTimeout(() => {
        console.log('WebSocket connect timed out');
        if (this.ws) {
          this.ws.close();
        }
      }, connectTimeoutMs);
    } catch (err) {
      console.log('WebSocket open failed', err);
      this.errorCounter += 1;
      this.scheduleReconnect();
    }
  }

  close() {
    this.onStateChangeListener = null;
    this.onMessageListener = null;

    if (this.ws) {
      this.ws.close(1000);
    }

    this.reconnectEnabled = false;
    this.retryTimeout = clearTimeout(this.retryTimeout);
    this.connectTimeout = clearTimeout(this.connectTimeout);
  }

  send(data) {
    this.ws.send(data);
  }

  updateStateListeners(state) {
    if (this.onStateChangeListener) {
      this.onStateChangeListener(state, this.retryCounter, this.errorCounter);
    }
  }

  scheduleReconnect() {
    const reconnect = this.reconnectEnabled && this.retryCounter <= maxRetries;

    this.updateStateListeners(reconnect ? States.Reconnecting : States.Closed);

    if (reconnect) {
      this.retryCounter += 1;

      this.retryTimeout = setTimeout(
        () => this.open(),
        Math.floor(Math.random() * 2 ** this.retryCounter * initialDelay),
      );
    }
  }

  onCloseCallback() {
    this.connectTimeout = clearTimeout(this.connectTimeout);
    this.scheduleReconnect();
  }

  onOpenCallback() {
    this.connectTimeout = clearTimeout(this.connectTimeout);
    this.updateStateListeners(States.Open);
    this.retryCounter = 0;
  }

  onErrorCallback(e) {
    this.errorCounter += 1;
    console.log('Websocket Error', e);
  }

  onMessageCallback(e) {
    if (this.onMessageListener) {
      this.onMessageListener(e);
    }
  }

  set onStateChange(callback) {
    this.onStateChangeListener = callback;
  }

  get onStateChange() {
    return this.onStateChangeListener;
  }

  set onMessage(callback) {
    this.onMessageListener = callback;
  }

  get onMessage() {
    return this.onMessageListener;
  }
}
